import { CSSProperties, ElementType, FC, Fragment, ReactNode } from "react";

import { ArrowRight, X } from "lucide-react";
import { cx } from "../../../utils/cx";
import styles from "./PunchupCampaignBanner.module.css";

export interface PunchupCampaignBannerTheme {
  background?: string;
  accent?: string;
  accentForeground?: string;
  glow?: string;
}

export interface PunchupCampaignBannerProps {
  artwork?: ReactNode;
  headline: ReactNode;
  headingAs?: ElementType;
  lineup?: string[];
  lineupTrailing?: string;
  ctaLabel: string;
  ctaHref: string;
  ctaExternal?: boolean;
  onCtaClick?: () => void;
  secondaryCtaLabel?: string;
  secondaryCtaHref?: string;
  onSecondaryCtaClick?: () => void;
  attribution?: ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
  theme?: PunchupCampaignBannerTheme;
  className?: string;
}

/**
 * The partner-campaign promo panel — the category's audience exception: a
 * campaign addresses every visitor, signed in or not. Horizontal on desktop,
 * stacked on mobile. Props and invariants: `CLAUDE.md`.
 */
export const PunchupCampaignBanner: FC<PunchupCampaignBannerProps> = ({
  artwork,
  headline,
  headingAs: Heading = "h2",
  lineup,
  lineupTrailing,
  ctaLabel,
  ctaHref,
  ctaExternal,
  onCtaClick,
  secondaryCtaLabel,
  secondaryCtaHref,
  onSecondaryCtaClick,
  attribution,
  onDismiss,
  dismissLabel = "Dismiss",
  theme,
  className,
}) => {
  // The partner's colours ride in as inline custom properties so no campaign
  // hex ever enters the tier's token CSS.
  const themeStyle = {
    ...(theme?.background && { "--punchup-campaign-bg": theme.background }),
    ...(theme?.accent && { "--punchup-campaign-accent": theme.accent }),
    ...(theme?.accentForeground && {
      "--punchup-campaign-accent-fg": theme.accentForeground,
    }),
    ...(theme?.glow && { "--punchup-campaign-glow": theme.glow }),
  } as CSSProperties;

  const hasLineup = Boolean(lineup?.length) || Boolean(lineupTrailing);

  return (
    <section className={cx(styles.banner, className)} style={themeStyle}>
      <div className={styles.glow} aria-hidden="true" />

      {onDismiss && (
        <button
          type="button"
          className={styles.dismiss}
          onClick={onDismiss}
          aria-label={dismissLabel}
        >
          <X className={styles.dismissIcon} aria-hidden="true" />
        </button>
      )}

      {artwork && <div className={styles.artwork}>{artwork}</div>}

      <div className={styles.content}>
        <Heading className={styles.headline}>{headline}</Heading>

        {hasLineup && (
          <p className={styles.lineup}>
            {lineup?.map((name, index) => (
              <Fragment key={name}>
                {index > 0 && (
                  <span className={styles.separator} aria-hidden="true">
                    ·
                  </span>
                )}
                <span>{name}</span>
              </Fragment>
            ))}
            {lineupTrailing && (
              <>
                {Boolean(lineup?.length) && (
                  <span className={styles.separator} aria-hidden="true">
                    ·
                  </span>
                )}
                <span className={styles.lineupTrailing}>{lineupTrailing}</span>
              </>
            )}
          </p>
        )}

        <div className={styles.footer}>
          {attribution && <p className={styles.attribution}>{attribution}</p>}
          <div className={styles.actions}>
            <a
              className={styles.cta}
              href={ctaHref}
              onClick={onCtaClick}
              {...(ctaExternal && {
                target: "_blank",
                rel: "noopener noreferrer",
              })}
            >
              {ctaLabel}
              <ArrowRight className={styles.ctaIcon} aria-hidden="true" />
            </a>
            {secondaryCtaLabel && secondaryCtaHref && (
              <a
                className={styles.ctaSecondary}
                href={secondaryCtaHref}
                onClick={onSecondaryCtaClick}
              >
                {secondaryCtaLabel}
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
